import { Divider, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShuttleRackParams } from '@thive/fddm-types';
import { degToRad, radToDeg } from 'three/src/math/MathUtils.js';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs';
import { NumUtils } from '@/modules/common/utils/NumUtils';
import { computeRectanglesBoundingBoxExtent } from '@/modules/common/helpers/boundingBox';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useReadSelectedTemplates } from '@/modules/floorplanDesign/hooks';
import {
  areasToFlatCollection,
  hasNoTemplates,
} from '@/modules/floorplanDesign/helpers/helpers';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { SHUTTLE_RACK } from '../constants';
import { TemplateDimensions } from './TemplateDimensions';

function ShuttleTemplateParametersComponent() {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = TpsServiceFactory.getInstance().createTpsService();
  const selectedTemplates = useReadSelectedTemplates();

  const shuttleGroup = useMemo(() => {
    if (hasNoTemplates(selectedTemplates)) return;

    const group = selectedTemplates.shuttleRacks[0];
    if (!group) return;

    return group;
  }, [selectedTemplates]);

  const newGenerationParams = useMemo(() => {
    if (!shuttleGroup) return;

    return shuttleGroup.params;
  }, [shuttleGroup]);

  const dimensions = useFloorplanDesignStore((state) => {
    if (!shuttleGroup) return;

    const areasInGroupIds = shuttleGroup.group.areaIds;
    const areasInGroups = areasToFlatCollection(state.getAreasByIds(areasInGroupIds));

    const rectangles = areasInGroups.flatMap((item) =>
      isHighwayArea(item) ? item.segments.flatMap(RectangleUtils.fromSegment) : [item.rectangle],
    );

    if (rectangles.length === 0) return null;

    const box = computeRectanglesBoundingBoxExtent(rectangles);
    return { x: box.x, y: box.y, length: box.width, width: box.height };
  });

  const regenerate = useCallback(
    async (params?: Partial<ShuttleRackParams>) => {
      if (!dimensions || !newGenerationParams || !shuttleGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      await tpsSvc.regenerateShuttleTemplate({
        ...shuttleGroup,
        params: { ...newGenerationParams, ...params },
      });

      loadingSvc.hideLoadContainer();
    },
    [dimensions, tpsSvc, shuttleGroup, newGenerationParams, loadingSvc],
  );

  const onNumColumnsChange = useCallback(
    (newValue: number) => {
      if (newValue < SHUTTLE_RACK.MIN.NUM_COLUMNS) {
        return {
          error: t('common:minimum_value', `Minimum ${SHUTTLE_RACK.MIN.NUM_COLUMNS}`, {
            minValue: SHUTTLE_RACK.MIN.NUM_COLUMNS,
          }),
        };
      }

      fddmSvc.setPreference('template.shuttle.numColumns', newValue);
      regenerate({ numColumns: newValue });
    },
    [fddmSvc, regenerate, t],
  );

  const onNumLevelsChange = useCallback(
    (newValue: number) => {
      if (newValue < SHUTTLE_RACK.MIN.NUM_LEVELS) {
        return {
          error: t('common:minimum_value', `Minimum ${SHUTTLE_RACK.MIN.NUM_LEVELS}`, {
            minValue: SHUTTLE_RACK.MIN.NUM_LEVELS,
          }),
        };
      }

      fddmSvc.setPreference('template.shuttle.numLevels', newValue);
      regenerate({ numLevels: newValue });
    },
    [fddmSvc, regenerate, t],
  );

  const onNumDeepChange = useCallback(
    (newValue: number) => {
      if (newValue < SHUTTLE_RACK.MIN.NUM_DEEP) {
        return {
          error: t('common:minimum_value', `Minimum ${SHUTTLE_RACK.MIN.NUM_DEEP}`, {
            minValue: SHUTTLE_RACK.MIN.NUM_DEEP,
          }),
        };
      }

      fddmSvc.setPreference('template.shuttle.numDeep', newValue);
      regenerate({ numDeep: newValue });
    },
    [fddmSvc, regenerate, t],
  );

  const onEntrySideChange = useCallback(
    (_: unknown, newValue: ShuttleRackParams['entrySide'] | null) => {
      if (!newValue || newValue === newGenerationParams?.entrySide) return;
      fddmSvc.setPreference('template.shuttle.entrySide', newValue);
      regenerate({ entrySide: newValue });
    },
    [fddmSvc, regenerate, newGenerationParams?.entrySide],
  );

  const onCenterXChange = useCallback(
    (newValue: number) => {
      regenerate({ centerX: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onCenterYChange = useCallback(
    (newValue: number) => {
      regenerate({ centerY: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onAngleChange = useCallback(
    (newValue: number ) => {
      regenerate({ angle: degToRad(newValue) });
    },
    [regenerate],
  );

  if (!dimensions || !newGenerationParams || !shuttleGroup) return null;

  return (
    <Stack spacing={1} divider={<Divider />}>
      <TemplateDimensions
        x={newGenerationParams.centerX}
        y={newGenerationParams.centerY}
        length={dimensions.length}
        width={dimensions.width}
        angle={radToDeg(newGenerationParams.angle)}
        onXChange={onCenterXChange}
        onYChange={onCenterYChange}
        onAngleChange={onAngleChange}
      />
      <Accordion
        title={t('interface:properties.template_title.shuttle_rack', 'Shuttle Rack')}
        unmountOnExit
        defaultExpanded
        sx={{ px: 2 }}
      >
        <Stack direction='column' spacing={1}>
          <PropertiesLabel i18nkey='interface:properties.template.columns'>
            <NumberInput
              value={newGenerationParams.numColumns}
              onChange={onNumColumnsChange}
              minValue={1}
              disabled={mode !== 'EDITABLE'}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.levels'>
            <NumberInput
              value={newGenerationParams.numLevels}
              onChange={onNumLevelsChange}
              disabled={mode !== 'EDITABLE'}
              minValue={2}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.units_deep'>
            <NumberInput
              value={newGenerationParams.numDeep}
              onChange={onNumDeepChange}
              minValue={1}
              disabled={mode !== 'EDITABLE'}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.entry_side.label'>
            <ToggleButtonGroup
              value={newGenerationParams.entrySide}
              exclusive
              onChange={onEntrySideChange}
              disabled={mode !== 'EDITABLE'}
            >
              <ToggleButton
                value='FRONT'
                aria-label='Enable shuttle rack entry from a single side'
                sx={{ p: 0, fontSize: '13px' }}
              >
                {t('interface:properties.template.entry_side.single')}
              </ToggleButton>
              <ToggleButton
                value='FRONT_AND_BACK'
                aria-label='Enable shuttle rack entry from both sides'
                sx={{ p: 0, fontSize: '13px' }}
              >
                {t('interface:properties.template.entry_side.both')}
              </ToggleButton>
            </ToggleButtonGroup>
          </PropertiesLabel>
        </Stack>
      </Accordion>
    </Stack>
  );
}

export const ShuttleTemplateParameters = memo(ShuttleTemplateParametersComponent);
